import type { ProductRole } from '@academy/domain';
import type {
  PrimaryWorkspaceRouteId,
  WorkspaceRouteMetadata,
} from '../routes/workspace-route-metadata';

type RoleNavigationItemDefinition = {
  id: PrimaryWorkspaceRouteId;
  label?: string;
};

type RoleNavigationGroupDefinition = {
  label: string;
  items: readonly RoleNavigationItemDefinition[];
};

export type RoleNavigationGroup = {
  label: string;
  items: readonly WorkspaceRouteMetadata[];
};

const item = (id: PrimaryWorkspaceRouteId, label?: string): RoleNavigationItemDefinition => ({
  id,
  ...(label ? { label } : {}),
});

export const roleNavigationDefinitions = {
  STUDENT: [
    { label: 'Today', items: [item('overview', 'Today')] },
    { label: 'Learning', items: [item('learning'), item('assessment', 'Progress & feedback')] },
    { label: 'Classes', items: [item('schedule'), item('deliveries')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Me', items: [item('privacy')] },
  ],
  GUARDIAN: [
    { label: 'Overview', items: [item('overview')] },
    { label: 'Learners', items: [item('learning'), item('assessment', 'Progress & feedback')] },
    { label: 'Schedule', items: [item('schedule')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Privacy', items: [item('privacy')] },
  ],
  TEACHER: [
    { label: 'Today', items: [item('overview', 'Today')] },
    { label: 'Teach', items: [item('schedule'), item('groups'), item('people')] },
    {
      label: 'Content',
      items: [item('curriculum'), item('presentations'), item('deliveries')],
    },
    { label: 'Review', items: [item('learning'), item('assessment')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Account & safety', items: [item('privacy')] },
  ],
  ACADEMIC_COORDINATOR: [
    { label: 'Today', items: [item('overview', 'Today')] },
    { label: 'People & groups', items: [item('people'), item('groups')] },
    { label: 'Academics', items: [item('curriculum'), item('schedule')] },
    { label: 'Delivery', items: [item('presentations'), item('deliveries')] },
    { label: 'Review', items: [item('learning'), item('assessment')] },
    { label: 'Communication & safety', items: [item('messages'), item('privacy')] },
  ],
  ACADEMY_ADMIN: [
    { label: 'Overview', items: [item('overview')] },
    { label: 'Community', items: [item('people'), item('groups'), item('access')] },
    {
      label: 'Academics',
      items: [item('curriculum'), item('schedule'), item('presentations'), item('deliveries')],
    },
    { label: 'Learning review', items: [item('learning'), item('assessment')] },
    { label: 'Operations', items: [item('operations'), item('privacy')] },
    { label: 'Messages', items: [item('messages')] },
  ],
  SUPPORT: [
    { label: 'Queue', items: [item('overview', 'Queue overview')] },
    { label: 'Access', items: [item('access')] },
    { label: 'Operations', items: [item('operations')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Account', items: [item('privacy', 'Privacy & safety')] },
  ],
  SAFETY_OFFICER: [
    { label: 'Safety queue', items: [item('overview', 'Safety overview')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Privacy & safety', items: [item('privacy')] },
  ],
  PRIVACY_OFFICER: [
    { label: 'Privacy queue', items: [item('overview', 'Privacy overview')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Privacy', items: [item('privacy')] },
  ],
  AUDITOR: [
    { label: 'Audit', items: [item('overview', 'Audit overview'), item('operations')] },
    { label: 'Messages', items: [item('messages')] },
    { label: 'Privacy', items: [item('privacy')] },
  ],
} as const satisfies Record<ProductRole, readonly RoleNavigationGroupDefinition[]>;

export const buildRoleNavigationGroups = (
  role: ProductRole | null,
  allowed: readonly WorkspaceRouteMetadata[],
): readonly RoleNavigationGroup[] => {
  if (!role) return [];
  const allowedById = new Map(allowed.map((route) => [route.id, route]));
  const included = new Set<PrimaryWorkspaceRouteId>();
  const groups = roleNavigationDefinitions[role].flatMap((group) => {
    const items = group.items.flatMap((definition) => {
      const route = allowedById.get(definition.id);
      if (!route) return [];
      included.add(route.id);
      return [{ ...route, label: definition.label ?? route.label }];
    });
    return items.length > 0 ? [{ label: group.label, items }] : [];
  });
  const remaining = allowed.filter((route) => !included.has(route.id));
  return remaining.length > 0 ? [...groups, { label: 'More', items: remaining }] : groups;
};
